import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";

import apiClient, { getApiErrorMessage, setApiClientAuthToken } from "@/lib/api";
import { resolveUserRole } from "@/lib/adminRoles";
import {
  TOKEN_KEY,
  USER_KEY,
  TRUSTED_DEVICE_TOKEN_KEY,
  clearStoredSession,
  onSessionExpired,
} from "@/lib/authSession";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [error, setError] = useState(null);
  const tokenRef = useRef(null);
  const authInitializedRef = useRef(false);

  const syncApiAuth = useCallback((nextToken) => {
    tokenRef.current = nextToken || null;
    setApiClientAuthToken(nextToken || null);
  }, []);

  const clearSession = useCallback(({ clearTrustedDevice = false } = {}) => {
    syncApiAuth(null);
    setToken(null);
    setUser(null);
    clearStoredSession({ clearTrustedDevice });
  }, [syncApiAuth]);

  const persistSession = useCallback((payload) => {
    const { access_token: accessToken, user: userData, trusted_device_token: trustedDeviceToken } = payload;

    syncApiAuth(accessToken);
    setToken(accessToken);
    setUser(userData);

    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));

    if (trustedDeviceToken) {
      localStorage.setItem(TRUSTED_DEVICE_TOKEN_KEY, trustedDeviceToken);
    }
  }, [syncApiAuth]);

  useEffect(() => {
    const unsubscribe = onSessionExpired(() => {
      syncApiAuth(null);
      setToken(null);
      setUser(null);
    });

    return () => {
      unsubscribe();
    };
  }, [syncApiAuth]);

  useEffect(() => {
    let cancelled = false;
    const storedToken = localStorage.getItem(TOKEN_KEY);
    const storedUser = localStorage.getItem(USER_KEY);

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);

        syncApiAuth(storedToken);
        setToken(storedToken);
        setUser(parsedUser);

        void apiClient.get("/admin/auth/me")
          .then((response) => {
            if (cancelled) {
              return;
            }

            setUser(response.data);
            localStorage.setItem(USER_KEY, JSON.stringify(response.data));
          })
          .catch((refreshError) => {
            if (cancelled) {
              return;
            }
            if (refreshError?.response?.status === 401) {
              clearSession();
            }
          });
      } catch {
        clearSession();
      }
    } else {
      syncApiAuth(null);
    }

    authInitializedRef.current = true;
    setAuthInitialized(true);

    return () => {
      cancelled = true;
    };
  }, [clearSession, syncApiAuth]);

  useEffect(() => {
    const interceptor = apiClient.interceptors.request.use(
      (config) => {
        const authToken = tokenRef.current || localStorage.getItem(TOKEN_KEY);

        if (authToken) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${authToken}`;
        }

        return config;
      },
      (requestError) => Promise.reject(requestError),
    );

    return () => {
      apiClient.interceptors.request.eject(interceptor);
    };
  }, []);

  useEffect(() => {
    const interceptor = apiClient.interceptors.response.use(
      (response) => response,
      (responseError) => {
        if (
          responseError.response?.status === 401 &&
          authInitializedRef.current
        ) {
          clearSession();
        }

        return Promise.reject(responseError);
      },
    );

    return () => {
      apiClient.interceptors.response.eject(interceptor);
    };
  }, [clearSession]);

  const login = useCallback(async (email, password) => {
    setError(null);

    try {
      const trustedDeviceToken = localStorage.getItem(TRUSTED_DEVICE_TOKEN_KEY);
      const response = await apiClient.post("/admin/auth/login", {
        email,
        password,
        trusted_device_token: trustedDeviceToken || undefined,
      });

      if (response.data.access_token) {
        persistSession(response.data);
        return {
          success: true,
          authenticated: true,
          role: resolveUserRole(response.data.user, response.data.access_token),
        };
      }

      return {
        success: true,
        authenticated: false,
        otpRequired: true,
        message: response.data.message,
        rateLimited: response.data.rate_limited || false,
        retryAfterSeconds: response.data.retry_after_seconds ?? null,
      };
    } catch (requestError) {
      const message = getApiErrorMessage(requestError, "Erreur de connexion");
      setError(message);
      return { success: false, error: message };
    }
  }, [persistSession]);

  const verifyLogin = useCallback(async (email, otp, trustDevice = false) => {
    setError(null);

    try {
      const response = await apiClient.post("/admin/auth/login/verify", {
        email,
        otp,
        trust_device: trustDevice,
      });

      persistSession(response.data);
      return {
        success: true,
        role: resolveUserRole(response.data.user, response.data.access_token),
      };
    } catch (requestError) {
      const message = getApiErrorMessage(requestError, "Code invalide ou expire");
      setError(message);
      return { success: false, error: message };
    }
  }, [persistSession]);

  const register = useCallback(async (email, password) => {
    setError(null);

    try {
      const response = await apiClient.post("/admin/auth/register", {
        email,
        password,
      });

      return {
        success: true,
        otpRequired: true,
        message: response.data.message,
        rateLimited: response.data.rate_limited || false,
        retryAfterSeconds: response.data.retry_after_seconds ?? null,
      };
    } catch (requestError) {
      const message = getApiErrorMessage(requestError, "Erreur d'inscription");
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const verifyRegistration = useCallback(async (email, otp, trustDevice = false) => {
    setError(null);

    try {
      const response = await apiClient.post("/admin/auth/verify", {
        email,
        otp,
        trust_device: trustDevice,
      });

      persistSession(response.data);
      return {
        success: true,
        role: resolveUserRole(response.data.user, response.data.access_token),
      };
    } catch (requestError) {
      const message = getApiErrorMessage(requestError, "Code invalide ou expire");
      setError(message);
      return { success: false, error: message };
    }
  }, [persistSession]);

  const logout = useCallback(() => {
    clearSession();
  }, [clearSession]);

  const revokeTrustedDevice = useCallback(async () => {
    const trustedDeviceToken = localStorage.getItem(TRUSTED_DEVICE_TOKEN_KEY);

    try {
      if (trustedDeviceToken && token) {
        await apiClient.post("/admin/auth/trusted-device/revoke", {
          trusted_device_token: trustedDeviceToken,
        });
      }
    } catch {
      // Best effort revocation.
    } finally {
      clearSession({ clearTrustedDevice: true });
    }

    return { success: true };
  }, [clearSession, token]);

  const value = {
    user,
    role: resolveUserRole(user, token),
    token,
    loading: !authInitialized,
    authInitialized,
    error,
    isAuthenticated: !!token && !!user,
    login,
    verifyLogin,
    register,
    verifyRegistration,
    logout,
    revokeTrustedDevice,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};

export default AuthContext;
